<script setup>
import { onMounted, ref, watch } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { ContractsAPI } from 'dashboard/api/talkviaContracts';
import { useAlert } from 'dashboard/composables';
import ContractSendDialog from '../contracts/ContractSendDialog.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const copy = {
  empty: 'Nenhum contrato vinculado a este contato.',
  open: 'Abrir contrato',
  send: 'Enviar link',
  sent: 'Link enviado na conversa.',
  separator: '·',
  expires: 'vence',
};

const contracts = ref([]);
const isLoading = ref(false);
const contractSendDialogRef = ref(null);

const statusLabel = status =>
  ({
    pending: 'Pendente',
    signed: 'Assinado',
    expired: 'Expirado',
    cancelled: 'Cancelado',
  })[status] || status;

const fetchContracts = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  try {
    const { data } = await ContractsAPI.get({ contact_id: props.contactId });
    contracts.value = data.contracts || [];
  } finally {
    isLoading.value = false;
  }
};

const openContract = contract => {
  window.open(contract.public_url, '_blank', 'noopener,noreferrer');
};

const sendContract = async contract => {
  contractSendDialogRef.value?.open(contract);
};

const handleSendContractLink = async ({ contract, payload }) => {
  await ContractsAPI.sendLink(contract.id, payload);
  useAlert(copy.sent);
  await fetchContracts();
};

watch(() => props.contactId, fetchContracts);
onMounted(fetchContracts);
</script>

<template>
  <div class="px-3 py-2">
    <div v-if="isLoading" class="flex items-center justify-center py-5">
      <Spinner />
    </div>
    <div
      v-else-if="!contracts.length"
      class="px-2 py-4 text-sm text-n-slate-10"
    >
      {{ copy.empty }}
    </div>
    <div v-else class="grid gap-2">
      <div
        v-for="contract in contracts"
        :key="contract.id"
        class="border border-n-weak bg-n-alpha-1 p-3"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
              {{ contract.template_name || 'Contrato' }}
            </p>
            <p class="mb-0 mt-1 text-xs text-n-slate-10">
              {{ statusLabel(contract.status) }} {{ copy.separator }}
              {{ copy.expires }} {{ contract.expires_at }}
            </p>
          </div>
          <div class="flex flex-shrink-0 gap-1">
            <button
              v-tooltip.top="copy.open"
              type="button"
              class="relative inline-flex size-8 items-center justify-center text-n-brand transition hover:bg-n-alpha-2"
              :title="copy.open"
              @click="openContract(contract)"
            >
              <span class="i-lucide-external-link size-4" />
              <span class="sr-only">{{ copy.open }}</span>
            </button>
            <button
              v-if="contract.status === 'pending'"
              v-tooltip.top="copy.send"
              type="button"
              class="relative inline-flex size-8 items-center justify-center text-n-brand transition hover:bg-n-alpha-2"
              :title="copy.send"
              @click="sendContract(contract)"
            >
              <span class="i-lucide-send size-4" />
              <span class="sr-only">{{ copy.send }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <ContractSendDialog
      ref="contractSendDialogRef"
      @send="handleSendContractLink"
    />
  </div>
</template>
